import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Animated, Easing, Platform, StyleSheet, View } from "react-native";

import { fonts } from "@/lib/theme";

const USE_NATIVE_DRIVER = Platform.OS !== "web";

const GHOST_CODES = ["I50.9", "J44", "R60", "R06", "E78", "I10", "Z79", "N18"] as const;

const GRID_PITCH = 22;

/**
 * Band tone — drives the entire visual: gradient wash, halo colors,
 * ghost-code color, scan-line gradient + duration. Same shape, same
 * internal layout for every tone.
 *
 *   teal   — validating / scanning (default — diagnoses analyzing,
 *            validation validating)
 *   amber  — errors-found (validation review needed)
 *   ok     — success (resolved, clear)
 *   err    — failed validation
 *   neutral — idle (diagnoses idle phase before scan)
 */
export type BandTone = "teal" | "amber" | "ok" | "err" | "neutral";

interface Props {
    /**
     * Legacy `phase` prop — kept for backward compat with DiagnosesChapter
     * which still passes "idle" / "analyzing" / "results". Maps internally
     * to a tone: idle/results → neutral, analyzing → teal.
     */
    phase?: "idle" | "analyzing" | "results";
    /**
     * New `tone` prop — overrides `phase` when set. Used by the validation
     * band flow which needs amber/ok/err in addition to teal.
     */
    tone?: BandTone;
    /** Faster scan-line loop (1.4s vs 4s) — used during active scanning. */
    scanFast?: boolean;
    children: ReactNode;
}

/**
 * Full-bleed band — breaks out of section padding to span screen width.
 * Houses any phase-driven lifecycle content (diagnoses scan, validation
 * review, etc.). All visual layers share between both surfaces.
 */
export function FullBleedBand({ phase, tone: toneProp, scanFast, children }: Props) {
    const tone: BandTone = toneProp ?? (phase === "analyzing" ? "teal" : "neutral");
    const fastSweep = scanFast ?? phase === "analyzing";

    const sweep = useRef(new Animated.Value(0)).current;
    const [bandHeight, setBandHeight] = useState(220);

    useEffect(() => {
        sweep.setValue(0);
        // Validating / scanning: 1.4s. Everything else: 4s ambient drift.
        const duration = fastSweep ? 1400 : 4000;
        const loop = Animated.loop(
            Animated.timing(sweep, {
                toValue: 1,
                duration,
                easing: Easing.inOut(Easing.ease),
                useNativeDriver: USE_NATIVE_DRIVER
            })
        );
        loop.start();
        return () => loop.stop();
    }, [fastSweep, sweep]);

    const visual = TONE_VISUALS[tone];

    return (
        <View
            style={[styles.band, visual.containerStyle]}
            onLayout={(e) => setBandHeight(e.nativeEvent.layout.height)}
        >
            <LinearGradient
                colors={visual.bgColors as [string, string]}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={StyleSheet.absoluteFill}
                pointerEvents="none"
            />

            <GridPattern width={500} height={bandHeight} pitch={GRID_PITCH} />

            <View
                pointerEvents="none"
                style={[styles.haloTopRight, { backgroundColor: visual.haloTopRight }]}
            />
            <View
                pointerEvents="none"
                style={[styles.haloBottomLeft, { backgroundColor: visual.haloBottomLeft }]}
            />

            <View style={styles.ghostLayer} pointerEvents="none">
                {GHOST_CODES.map((code, i) => (
                    <GhostCode
                        key={code}
                        code={code}
                        index={i}
                        color={visual.ghostColor}
                        active={tone === "teal" || tone === "amber"}
                    />
                ))}
            </View>

            <Animated.View
                style={[
                    styles.scanLine,
                    {
                        height: fastSweep ? 3 : 2,
                        transform: [
                            {
                                translateY: sweep.interpolate({
                                    inputRange: [0, 0.5, 1],
                                    outputRange: [0, Math.max(40, bandHeight - 4), 0]
                                })
                            }
                        ]
                    }
                ]}
                pointerEvents="none"
            >
                <LinearGradient
                    colors={visual.scanColors as [string, string, string, string]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={StyleSheet.absoluteFill}
                />
            </Animated.View>

            <View style={styles.content}>{children}</View>
        </View>
    );
}

interface ToneVisuals {
    containerStyle: { borderColor: string };
    bgColors: [string, string];
    haloTopRight: string;
    haloBottomLeft: string;
    ghostColor: string;
    scanColors: [string, string, string, string];
}

const TONE_VISUALS: Record<BandTone, ToneVisuals> = {
    teal: {
        containerStyle: { borderColor: "rgba(69,210,176,0.30)" },
        bgColors: ["rgba(69,210,176,0.12)", "rgba(231,72,245,0.10)"],
        haloTopRight: "rgba(69,210,176,0.18)",
        haloBottomLeft: "rgba(231,72,245,0.16)",
        ghostColor: "rgba(69,210,176,0.55)",
        scanColors: [
            "rgba(69,210,176,0)",
            "rgba(69,210,176,0.65)",
            "rgba(231,72,245,0.65)",
            "rgba(231,72,245,0)"
        ]
    },
    amber: {
        containerStyle: { borderColor: "rgba(245,158,11,0.30)" },
        bgColors: ["rgba(245,158,11,0.12)", "rgba(225,29,72,0.06)"],
        haloTopRight: "rgba(245,158,11,0.18)",
        haloBottomLeft: "rgba(231,72,245,0.12)",
        ghostColor: "rgba(245,158,11,0.55)",
        scanColors: [
            "rgba(245,158,11,0)",
            "rgba(245,158,11,0.65)",
            "rgba(231,72,245,0.45)",
            "rgba(231,72,245,0)"
        ]
    },
    ok: {
        containerStyle: { borderColor: "rgba(16,185,129,0.30)" },
        bgColors: ["rgba(16,185,129,0.14)", "rgba(69,210,176,0.10)"],
        haloTopRight: "rgba(16,185,129,0.18)",
        haloBottomLeft: "rgba(69,210,176,0.18)",
        ghostColor: "rgba(16,185,129,0.55)",
        scanColors: [
            "rgba(16,185,129,0)",
            "rgba(16,185,129,0.65)",
            "rgba(69,210,176,0.55)",
            "rgba(69,210,176,0)"
        ]
    },
    err: {
        containerStyle: { borderColor: "rgba(225,29,72,0.30)" },
        bgColors: ["rgba(225,29,72,0.12)", "rgba(245,158,11,0.06)"],
        haloTopRight: "rgba(225,29,72,0.18)",
        haloBottomLeft: "rgba(245,158,11,0.16)",
        ghostColor: "rgba(225,29,72,0.55)",
        scanColors: [
            "rgba(225,29,72,0)",
            "rgba(225,29,72,0.65)",
            "rgba(245,158,11,0.45)",
            "rgba(245,158,11,0)"
        ]
    },
    neutral: {
        containerStyle: { borderColor: "rgba(69,210,176,0.30)" },
        bgColors: ["rgba(69,210,176,0.10)", "rgba(231,72,245,0.08)"],
        haloTopRight: "rgba(69,210,176,0.18)",
        haloBottomLeft: "rgba(231,72,245,0.16)",
        ghostColor: "rgba(11,11,31,0.18)",
        scanColors: [
            "rgba(69,210,176,0)",
            "rgba(69,210,176,0.45)",
            "rgba(231,72,245,0.45)",
            "rgba(231,72,245,0)"
        ]
    }
};

function GridPattern({ width, height, pitch }: { width: number; height: number; pitch: number }) {
    const verticalLines: number[] = [];
    for (let x = 0; x < width; x += pitch) {
        verticalLines.push(x);
    }
    const horizontalLines: number[] = [];
    for (let y = 0; y < height; y += pitch) {
        horizontalLines.push(y);
    }
    return (
        <View pointerEvents="none" style={StyleSheet.absoluteFill}>
            {verticalLines.map((x) => (
                <View key={`v-${x}`} style={[styles.gridV, { left: x }]} />
            ))}
            {horizontalLines.map((y) => (
                <View key={`h-${y}`} style={[styles.gridH, { top: y }]} />
            ))}
        </View>
    );
}

function GhostCode({
    code,
    index,
    color,
    active
}: {
    code: string;
    index: number;
    color: string;
    active: boolean;
}) {
    const drift = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.delay(index * 250),
                Animated.timing(drift, {
                    toValue: 1,
                    duration: 4000,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: USE_NATIVE_DRIVER
                }),
                Animated.timing(drift, {
                    toValue: 0,
                    duration: 4000,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: USE_NATIVE_DRIVER
                })
            ])
        );
        loop.start();
        return () => loop.stop();
    }, [drift, index]);

    const left = 14 + ((index * 67) % 280);
    const top = 18 + ((index * 41) % 200);
    return (
        <Animated.Text
            style={[
                styles.ghostCode,
                {
                    left,
                    top,
                    color,
                    opacity: drift.interpolate({
                        inputRange: [0, 1],
                        outputRange: active ? [0.45, 0.85] : [0.35, 0.7]
                    }),
                    transform: [
                        {
                            translateX: drift.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, 3]
                            })
                        },
                        {
                            translateY: drift.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0, -4]
                            })
                        }
                    ]
                }
            ]}
        >
            {code}
        </Animated.Text>
    );
}

const styles = StyleSheet.create({
    band: {
        position: "relative",
        marginHorizontal: -22,
        paddingHorizontal: 22,
        paddingVertical: 34,
        overflow: "hidden",
        borderTopWidth: 1,
        borderBottomWidth: 1
    },
    gridV: {
        position: "absolute",
        top: 0,
        bottom: 0,
        width: 1,
        backgroundColor: "rgba(11,11,31,0.05)"
    },
    gridH: {
        position: "absolute",
        left: 0,
        right: 0,
        height: 1,
        backgroundColor: "rgba(11,11,31,0.05)"
    },
    haloTopRight: {
        position: "absolute",
        top: -40,
        right: -40,
        width: 220,
        height: 220,
        borderRadius: 110,
        ...Platform.select({
            ios: {
                shadowColor: "#45D2B0",
                shadowOpacity: 0.45,
                shadowRadius: 80,
                shadowOffset: { width: 0, height: 0 }
            },
            android: { elevation: 12 },
            default: {}
        })
    },
    haloBottomLeft: {
        position: "absolute",
        bottom: -40,
        left: -40,
        width: 220,
        height: 220,
        borderRadius: 110,
        ...Platform.select({
            ios: {
                shadowColor: "#E748F5",
                shadowOpacity: 0.4,
                shadowRadius: 80,
                shadowOffset: { width: 0, height: 0 }
            },
            android: { elevation: 12 },
            default: {}
        })
    },
    ghostLayer: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
    },
    ghostCode: {
        position: "absolute",
        fontFamily: fonts.bold,
        fontSize: 11
    },
    scanLine: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0
    },
    content: {
        position: "relative",
        zIndex: 2
    }
});
